import React from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import uselessFacts from '../data/uselessFacts.json';
import animalFacts from '../data/animalFacts.json';
import weirdScience from '../data/weirdScience.json';
import pointlessRecords from '../data/pointlessRecords.json';
import uselessInventions from '../data/uselessInventions.json';
import numberEncyclopedia from '../data/numberEncyclopedia.json';
import LikeButton from '../components/LikeButton';
import ShareButton from '../components/ShareButton';

interface ContentPageProps {
  type: 'fact' | 'animal' | 'science' | 'record' | 'invention' | 'number';
}

const ContentPage: React.FC<ContentPageProps> = ({ type }) => {
  const { slug } = useParams<{ slug: string }>();

  const getContent = () => {
    // Decode and normalize the slug
    const decodedSlug = decodeURIComponent(slug || '').toLowerCase();
    
    const normalizeTitle = (title: string) => title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    switch (type) {
      case 'fact':
        return uselessFacts.facts.find(f => normalizeTitle(f.fact) === decodedSlug);
      case 'animal':
        return animalFacts.facts.find(f => normalizeTitle(f) === decodedSlug);
      case 'science':
        return weirdScience.facts.find(f => normalizeTitle(f) === decodedSlug);
      case 'record':
        return pointlessRecords.records.find(r => normalizeTitle(r.title) === decodedSlug);
      case 'invention':
        return uselessInventions.inventions.find(i => normalizeTitle(i.title) === decodedSlug);
      case 'number':
        return numberEncyclopedia.numbers.find(n => normalizeTitle(n.title) === decodedSlug);
      default:
        return null;
    }
  };

  const content = getContent();
  if (!content) return <div>Content not found</div>;

  const getTitle = () => {
    switch (type) {
      case 'fact':
        return 'Useless Fact';
      case 'animal':
        return 'Animal Fact';
      case 'science':
        return 'Weird Science';
      case 'record':
        return 'Pointless Record';
      case 'invention':
        return 'Useless Invention';
      case 'number':
        return 'Number Encyclopedia';
    }
  };

  const getStructuredData = () => {
    const baseData = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: getTitle(),
      datePublished: new Date().toISOString(),
      dateModified: new Date().toISOString(),
      publisher: {
        '@type': 'Organization',
        name: 'The Most Useless Website',
        url: 'https://themostuselesswebsite.com'
      }
    };

    switch (type) {
      case 'record':
        return {
          ...baseData,
          headline: content.title,
          description: content.description,
          author: content.holder,
          dateCreated: `${content.year}-01-01`
        };
      case 'invention':
        return {
          ...baseData,
          headline: content.title,
          description: content.description,
          inventor: content.inventor,
          dateCreated: `${content.year}-01-01`
        };
      default:
        return {
          ...baseData,
          description: typeof content === 'string' ? content : content.fact || content.title
        };
    }
  };

  const getMetaDescription = () => {
    if (typeof content === 'string') return content;
    if ('description' in content) return content.description;
    if ('fact' in content) return content.fact;
    if ('title' in content) return content.title;
    return '';
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <SEO
        title={`${getTitle()} - ${typeof content === 'string' ? content : content.title || content.fact}`}
        description={getMetaDescription()}
        type="article"
        jsonLd={getStructuredData()}
      />

      <div className="card p-8">
        {type === 'record' || type === 'invention' || type === 'number' ? (
          <>
            <h1 className="text-3xl font-bold gradient-text mb-6">{content.title}</h1>
            <p className="text-gray-300 mb-6">{content.description}</p>
            {content.facts && (
              <ul className="space-y-4 mb-6">
                {content.facts.map((fact: string, index: number) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-purple-400">•</span>
                    <span className="text-gray-300">{fact}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="text-gray-300 text-lg mb-6">{typeof content === 'string' ? content : content.fact}</p>
        )}

        <div className="flex items-center space-x-4">
          <LikeButton type={type} content={typeof content === 'string' ? content : content.title || content.fact} />
          <ShareButton 
            title={`Check out this ${getTitle()}!`}
            text={typeof content === 'string' ? content : content.title || content.fact}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentPage;